import { invitationExpiryText } from "./invitation-expiry";
import { AccountAvatar, AccountNavigationContext } from "./AccountNavigation";
import { usePageState } from "./Navigation";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { api, type ProjectContractorAccount } from "./api";
import { legacyFetch } from "./legacy-api";

type Contractor = {
  id: string;
  company: string;
  trade: string;
  contactName: string;
  telephone: string;
  email: string;
  fullSnagListReadOnly: boolean;
  accountCount: number;
  initials: string;
  accessCode: string;
};

type InvitationKind = "company" | "individual";

export default function ContractorInvitations({
  projectId,
  projectName,
  contractors,
  onOpenSetup,
  onRefresh,
  projects,
  onProjectChange,
}: {
  projectId: string;
  projectName: string;
  contractors: Contractor[];
  onOpenSetup: () => void;
  onRefresh: () => Promise<void>;
  projects: Array<{ id: string; name: string }>;
  onProjectChange: (projectId: string) => Promise<void>;
}) {
  const accountNavigation = useContext(AccountNavigationContext);
  const invitationBuilderRef = useRef<HTMLElement>(null);
  const [contractorId, setContractorId] = useState(contractors[0]?.id ?? "");
  const [kind, setKind] = useState<InvitationKind>("company");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [working, setWorking] = useState(false);
  const [accessWorking, setAccessWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [accountCompany, setAccountCompany] = usePageState<Contractor | null>("invitation.company", null);
  const [accounts, setAccounts] = useState<ProjectContractorAccount[]>([]);
  const [accountsWorking, setAccountsWorking] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [temporaryPasswordAccount, setTemporaryPasswordAccount] = useState<ProjectContractorAccount | null>(null);
  const [shareAccount, setShareAccount] = useState<ProjectContractorAccount | null>(null);
  const [shareAccountQr, setShareAccountQr] = useState("");
  const selected = useMemo(() => contractors.find((item) => item.id === contractorId) ?? null, [contractorId, contractors]);
  const projectAccounts = accounts.filter((account) => account.projectAccess);
  const allProjectAccountsReadOnly = Boolean(projectAccounts.length) && projectAccounts.every((account) => account.fullSnagListReadOnly);

  useEffect(() => {
    if (!link) { setQrCode(""); return; }
    let active = true;
    void QRCode.toDataURL(link, {
      width: 320,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#102d24", light: "#ffffff" },
    }).then((value) => { if (active) setQrCode(value); });
    return () => { active = false; };
  }, [link]);

  function resetGenerated() {
    setLink("");
    setExpiresAt("");
    setQrCode("");
    setMessage("");
  }

  async function createInvitation() {
    if (!selected) { setMessage("Choose a contractor company first"); return; }
    if (kind === "individual" && (!firstName.trim() || !lastName.trim())) { setMessage("Enter the person's first and last name"); return; }
    setWorking(true); setMessage(""); setLink("");
    try {
      const response = await legacyFetch("/api/manager/contractor-invitations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          contractorCompanyId: selected.id,
          kind,
          ...(kind === "individual" ? {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            ...(email.trim() ? { email: email.trim() } : {}),
            ...(telephone.trim() ? { telephone: telephone.trim() } : {}),
          } : {}),
        }),
      });
      const result = await response.json() as { token?: string; expiresAt?: string; error?: string };
      if (!response.ok || !result.token) throw new Error(result.error || "The invitation could not be created");
      const invitationUrl = new URL(window.location.href);
      invitationUrl.search = "";
      invitationUrl.hash = "";
      invitationUrl.searchParams.set("invite", result.token);
      setLink(invitationUrl.toString());
      setExpiresAt(result.expiresAt ?? "");
      setMessage(kind === "company" ? "Company invitation ready to share" : "Individual invitation ready to share");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The invitation could not be created");
    } finally {
      setWorking(false);
    }
  }

  async function changeFullListAccess(contractor: Contractor, checked: boolean) {
    setAccessWorking(true); setMessage(""); resetGenerated();
    try {
      const response = await legacyFetch("/api/manager/contractor-access", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, contractorCompanyId: contractor.id, fullSnagListReadOnly: checked }),
      });
      const result = await response.json() as { error?: string };
      if (!response.ok) throw new Error(result.error || "Access could not be changed");
      await onRefresh();
      setAccountCompany((current) => current?.id === contractor.id ? { ...current, fullSnagListReadOnly: checked } : current);
      setAccounts((current) => current.map((account) => account.projectAccess ? { ...account, fullSnagListReadOnly: checked } : account));
      setMessage(checked ? "Full snag list enabled as read-only" : "Access returned to assigned snags only");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Access could not be changed");
    } finally { setAccessWorking(false); }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setMessage("Invitation link copied");
    } catch {
      setMessage("Copy was blocked by the browser. Select the link below and copy it manually.");
    }
  }

  async function shareLink() {
    if (!selected || !link) return;
    if (!navigator.share) { await copyLink(); return; }
    try {
      await navigator.share({
        title: `${selected.company} · Revival Site Snag invitation`,
        text: contractorInvitationMessage(selected.company, projectName, kind, firstName, email, link, expiresAt),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setMessage("Sharing was not available. You can copy the link instead.");
    }
  }

  async function openAccounts(contractor: Contractor) {
    if (accountNavigation) { accountNavigation.openCompany(contractor.id); return; }
    setAccountCompany(contractor); setAccounts([]); setTemporaryPassword(""); setTemporaryPasswordAccount(null); setShareAccount(null); setShareAccountQr(""); setAccountsWorking("load"); setMessage("");
    try { setAccounts(await api.projectContractorAccounts(projectId, contractor.id)); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Accounts could not be opened"); }
    finally { setAccountsWorking(""); }
  }

  async function changeAccountAccess(account: ProjectContractorAccount, active: boolean, fullSnagListReadOnly = account.fullSnagListReadOnly) {
    setAccountsWorking(`access:${account.id}`); setMessage("");
    try {
      const updated = await api.setProjectContractorAccountAccess(projectId, account.id, active, fullSnagListReadOnly);
      setAccounts((current) => current.map((item) => item.id === account.id ? updated : item));
      setTemporaryPassword(""); setTemporaryPasswordAccount(null);
      setMessage(active ? `${account.name} can access this project` : `${account.name} was removed from this project`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "This person's project access could not be changed"); }
    finally { setAccountsWorking(""); }
  }

  async function resetAccountPassword(account: ProjectContractorAccount) {
    if (!window.confirm(`Reset ${account.name}'s password? Their current password will stop working immediately.`)) return;
    setAccountsWorking(account.id); setTemporaryPassword(""); setTemporaryPasswordAccount(null); setMessage("");
    try {
      const result = await api.resetProjectContractorPassword(projectId, account.id);
      setTemporaryPassword(result.temporaryPassword);
      setTemporaryPasswordAccount(account);
      setMessage(`Reset password created for ${account.name}. Copy it now; it is shown once.`);
      window.setTimeout(() => document.getElementById(`password-result-${account.id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 0);
    } catch (error) { setMessage(error instanceof Error ? error.message : "The password could not be reset"); }
    finally { setAccountsWorking(""); }
  }

  async function showAccountSignIn(account: ProjectContractorAccount) {
    const signInUrl = window.location.origin;
    setShareAccount(account); setShareAccountQr(""); setMessage("");
    try {
      setShareAccountQr(await QRCode.toDataURL(signInUrl, {
        width: 260, margin: 2, errorCorrectionLevel: "M", color: { dark: "#102d24", light: "#ffffff" },
      }));
      window.setTimeout(() => document.getElementById(`signin-result-${account.id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 0);
    } catch { setMessage("The sign-in QR code could not be created"); }
  }

  async function shareAccountSignIn(account: ProjectContractorAccount) {
    const text = `Hello ${account.name},\n\nOpen Revival Site Snag and sign in using your existing account:\n${window.location.origin}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Revival Site Snag sign-in", text }); return; }
      catch (error) { if (error instanceof DOMException && error.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(text); setMessage("Sign-in link copied and ready to resend"); }
    catch { setMessage("Sharing was blocked. Copy the sign-in address manually."); }
  }

  function openInvitationBuilder(selectedContractorId: string, nextKind: InvitationKind = "company") {
    setContractorId(selectedContractorId); setKind(nextKind); setAccountCompany(null); resetGenerated();
    window.requestAnimationFrame(() => invitationBuilderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  async function shareTemporaryPassword() {
    if (!temporaryPassword || !temporaryPasswordAccount) return;
    const text = temporaryPasswordMessage(temporaryPasswordAccount, temporaryPassword);
    if (navigator.share) {
      try { await navigator.share({ title: "Revival Site Snag password reset", text }); return; }
      catch (error) { if (error instanceof DOMException && error.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(text); setMessage("Password reset details copied and ready to share"); }
    catch { setMessage("Sharing was blocked. Copy the reset password manually."); }
  }

  if (!contractors.length) return <section className="invitations-page">
    <div className="page-title-row"><div><p className="eyebrow">CONTROLLED ACCESS</p><h1>Invitations</h1><p>Add a contractor company before creating access.</p></div></div>
    <div className="invitations-empty"><strong>No contractors on this job yet</strong><p>Company name and trade are all you need to get started.</p><button className="primary-button" onClick={onOpenSetup} type="button">Add a contractor</button></div>
  </section>;

  return <section className="invitations-page">
    <div className="page-title-row"><div><p className="eyebrow">CONTROLLED ACCESS</p><h1>Invitations</h1><p>Create a link for a contractor company, then share it by phone, email, copy or QR code.</p></div></div>
    <label className="invitation-project-select">Invitations for project<select disabled={working} value={projectId} onChange={(event) => void onProjectChange(event.target.value)}>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label>


    <div className="invitations-layout">
      <article className="invitation-builder" ref={invitationBuilderRef}>
        <header><p className="eyebrow">CREATE ACCESS</p><h2>Choose who the link is for</h2></header>
        <label className="invitation-select">Contractor company<select disabled={working} value={contractorId} onChange={(event) => { setContractorId(event.target.value); resetGenerated(); }}>{contractors.map((contractor) => <option key={contractor.id} value={contractor.id}>{contractor.company} · {contractor.trade}</option>)}</select></label>
        <div className="invitation-kind" role="radiogroup" aria-label="Invitation type">
          <button disabled={working} aria-pressed={kind === "company"} className={kind === "company" ? "active" : ""} onClick={() => { setKind("company"); setEmail(""); setFirstName(""); setLastName(""); setTelephone(""); resetGenerated(); }} type="button"><strong>Company team link</strong><span>Share with any number of team members. Each person creates their own named account.</span></button>
          <button disabled={working} aria-pressed={kind === "individual"} className={kind === "individual" ? "active" : ""} onClick={() => { setKind("individual"); resetGenerated(); }} type="button"><strong>One person only</strong><span>One account for the named person. The link works once; send it only to them.</span></button>
        </div>
        {kind === "individual" && <div className="invitation-person-fields">
          <label>First name <b>Required</b><input autoComplete="given-name" value={firstName} onChange={(event) => { setFirstName(event.target.value); resetGenerated(); }} placeholder="First name" /></label>
          <label>Last name <b>Required</b><input autoComplete="family-name" value={lastName} onChange={(event) => { setLastName(event.target.value); resetGenerated(); }} placeholder="Last name" /></label>
          <label>Email <small>Optional</small><input autoComplete="email" value={email} onChange={(event) => { setEmail(event.target.value); resetGenerated(); }} placeholder="name@company.co.uk" type="email" /></label>
          <label>Phone <small>Optional</small><input autoComplete="tel" inputMode="tel" value={telephone} onChange={(event) => { setTelephone(event.target.value); resetGenerated(); }} placeholder="Mobile or telephone" type="tel" /></label>
        </div>}
        <button className="primary-button invitation-create" disabled={working || !contractorId || (kind === "individual" && (!firstName.trim() || !lastName.trim()))} onClick={() => void createInvitation()} type="button">{working ? "Creating…" : kind === "company" ? "Create company invitation" : "Create individual invitation"}</button>
        <small className="invitation-expiry-copy">Individual links expire after seven days. A company team link stays the same when it is opened or shared again and remains valid for up to one year.</small>
        {message && <div className={link ? "invitation-message success" : "invitation-message"}>{message}</div>}
      </article>

      <article className={link ? "invitation-share ready" : "invitation-share"}>
        {!link && <div className="share-placeholder"><span>QR</span><strong>Your share options will appear here</strong><p>Create an invitation to copy its link, open your phone's share menu or show a scannable QR code.</p></div>}
        {link && selected && <>
          <header><p className="eyebrow">READY TO SHARE</p><h2>{selected.company}</h2><p>{kind === "company" ? "Team link · reusable for named accounts" : `One-time link · ${firstName} ${lastName}${email ? ` · ${email}` : telephone ? ` · ${telephone}` : ""}`}</p></header>
          {qrCode && <div className="invitation-qr"><img alt={`QR code for ${selected.company} invitation`} src={qrCode} /><span>Scan with a phone camera</span></div>}
          <label className="invitation-link">Invitation link<input readOnly value={link} onFocus={(event) => event.currentTarget.select()} /></label>
          <div className="share-actions"><button onClick={() => void copyLink()} type="button">Copy link</button><button className="primary-button" onClick={() => void shareLink()} type="button">Share</button><a href={`mailto:${encodeURIComponent(kind === "individual" ? email : selected.email)}?subject=${encodeURIComponent(`${projectName} · Revival Site Snag access`)}&body=${encodeURIComponent(contractorInvitationMessage(selected.company, projectName, kind, firstName, email, link, expiresAt))}`} rel="noreferrer" target="_blank">Email invitation</a></div>
          {qrCode && <a className="download-qr" download={`${selected.company.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}-site-audit-qr.png`} href={qrCode}>Download QR code</a>}
          {expiresAt && <p className="invitation-expiry" role="status">{invitationExpiryText(expiresAt)}</p>}
        </>}
      </article>
    </div>

    <div className="invitation-security-note"><span>✓</span><div><strong>Each person still creates their own named account</strong><p>A company link can be shared across that contractor's team. Every account sees only snagging assigned to the selected company.</p></div></div>

    <section className="contractor-account-list"><div className="section-heading"><div><p className="eyebrow">CONTRACTOR COMPANIES</p><h2>Account status</h2></div><button className="text-button" onClick={onOpenSetup} type="button">Add contractor <span>→</span></button></div><div>{contractors.map((contractor) => <article key={contractor.id}><AccountAvatar className="large-team-avatar" name={contractor.company} initials={contractor.initials} companyId={contractor.id} /><div className="company-account-summary"><strong>{contractor.company}</strong><small>{contractor.trade}{contractor.contactName ? ` · ${contractor.contactName}` : ""}</small></div><button className={contractor.accountCount ? "account-status-button has-accounts" : "account-status-button"} onClick={() => void openAccounts(contractor)} type="button">{contractor.accessCode}</button></article>)}</div></section>
    {accountCompany && <div className="account-detail-layer" role="dialog" aria-modal="true" aria-label={`${accountCompany.company} active accounts`}>
      <button aria-label="Close accounts" className="account-detail-backdrop" onClick={() => setAccountCompany(null)} type="button" />
      <section>
        <header>
          <div><p className="eyebrow">ACTIVE ACCOUNTS</p><h2>{accountCompany.company}</h2><p>{projectName}</p></div>
          <button aria-label="Close accounts" onClick={() => setAccountCompany(null)} type="button">×</button>
        </header>
        <div className="account-all-access-control">
          <span><strong>All-site read-only access</strong><small>Choose this for individual members below, or apply the same setting to everyone who can access this project.</small></span>
          <button disabled={accessWorking || !projectAccounts.length} onClick={() => void changeFullListAccess(accountCompany, !allProjectAccountsReadOnly)} type="button">{accessWorking ? "Saving…" : allProjectAccountsReadOnly ? "Assigned snags only for all" : "Allow for all members"}</button>
        </div>
        {accountsWorking === "load" ? <p>Opening accounts…</p> : <div className="invitation-account-users">{accounts.map((account) => {
          const accessBusy = accountsWorking === `access:${account.id}`;
          const passwordBusy = accountsWorking === account.id;
          return <article className={!account.projectAccess || !account.active ? "account-access-removed" : ""} key={account.id}>
            <AccountAvatar className="large-team-avatar" name={account.name} initials={account.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()} userId={account.id} companyId={account.contractorCompanyId} />
            <div className="account-user-summary"><strong>{account.name}</strong><small>{account.email}{account.telephone ? ` · ${account.telephone}` : ""}</small></div>
            <b>{!account.active ? "Account inactive" : account.projectAccess ? "Project access" : "Project removed"}</b>
            <label className="account-permission-row"><input checked={account.projectAccess && account.fullSnagListReadOnly} disabled={!account.projectAccess || accessBusy} onChange={(event) => void changeAccountAccess(account, true, event.target.checked)} type="checkbox" /><span><strong>All site snags read-only</strong><small>Assigned snags remain actionable.</small></span></label>
            <div className="account-user-actions">
              <button className="resend-account-link" onClick={() => void showAccountSignIn(account)} type="button">Resend link + QR</button>
              <button className="reset-password-button" disabled={passwordBusy || !account.active} onClick={() => void resetAccountPassword(account)} type="button">{passwordBusy ? "Resetting…" : "Reset password"}</button>
              <button className="remove-account-project-access" disabled={accessBusy || !account.active} onClick={() => void changeAccountAccess(account, !account.projectAccess, account.projectAccess ? false : account.fullSnagListReadOnly)} type="button">{accessBusy ? "Saving…" : account.projectAccess ? "Remove project access" : "Restore project access"}</button>
            </div>
            {shareAccount?.id === account.id && <div className="account-inline-result account-signin-result" id={`signin-result-${account.id}`}>
              {shareAccountQr && <img alt={`Sign-in QR code for ${account.name}`} src={shareAccountQr} />}
              <div><strong>Sign-in link for {account.name}</strong><small>The QR and link open the login page only. They do not contain an email address or password.</small><code>{window.location.origin}</code><div><button onClick={() => void navigator.clipboard.writeText(window.location.origin)} type="button">Copy link</button><button onClick={() => void shareAccountSignIn(account)} type="button">Share link</button></div></div>
            </div>}
            {temporaryPassword && temporaryPasswordAccount?.id === account.id && <div className="account-inline-result temporary-password" id={`password-result-${account.id}`}><div><strong>Reset password for {account.name}</strong><small>Shown once. Their existing email address is deliberately omitted from the shared message.</small></div><code>{temporaryPassword}</code><button onClick={() => void navigator.clipboard.writeText(temporaryPassword)} type="button">Copy password</button><button onClick={() => void shareTemporaryPassword()} type="button">Share reset access</button></div>}
          </article>;
        })}{!accounts.length && <p>No registered accounts were found for this company.</p>}</div>}
        <div className="account-share-signin"><button onClick={() => void navigator.clipboard.writeText(`Sign in to Revival Site Snag at ${window.location.origin}`)} type="button">Copy sign-in address</button><button onClick={() => openInvitationBuilder(accountCompany.id)} type="button">Re-share company link</button></div>
      </section>
    </div>}
  </section>;
}

function temporaryPasswordMessage(account: ProjectContractorAccount, temporaryPassword: string): string {
  return `Hello ${account.name},\n\nYour Revival Site Snag password has been reset.\n\nSign in: ${window.location.origin}\nTemporary password: ${temporaryPassword}\n\nUse the email address already registered to your account. After signing in, you will be required to create your own new password before opening any projects.`;
}

function contractorInvitationMessage(company: string, projectName: string, kind: InvitationKind, firstName: string, accountEmail: string, link: string, expiresAt: string): string {
  const greeting = kind === "individual" && firstName.trim() ? `Hello ${firstName.trim()},` : "Hello,";
  const sharing = kind === "company"
    ? "This company team link may be distributed to your team. Each person must use it to create their own named account."
    : "This is a private, one-time link for your named account. Please do not forward it.";
  const signIn = kind === "individual" && accountEmail.trim()
    ? `This invitation is reserved for ${accountEmail.trim()}. Use that email when you sign in.`
    : "The setup page will ask for the work email you will use to sign in.";
  const expiry = expiresAt ? invitationExpiryText(expiresAt) : "Check the invitation expiry before sharing.";
  return `${greeting}\n\nYou have been invited to access snagging assigned to ${company} on ${projectName}.\n\n${sharing}\n${signIn}\n\nTo get access:\n1. Open the secure link below.\n2. Enter your name and work email if requested.\n3. Create and confirm a password.\n4. Sign in to see the projects and snags assigned to your company.\n\n${link}\n\n${expiry}.`;
}
